import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { estatisticasAPI } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Users, 
  Package, 
  FileText, 
  UserCog, 
  Plus,
  ArrowRight,
  Calendar
} from 'lucide-react';

export const Dashboard = () => {
  const [stats, setStats] = useState({
    total_clientes: 0,
    total_produtos: 0,
    total_receitas: 0,
    total_utilizadores: 0,
    receitas_recentes: []
  });
  const [loading, setLoading] = useState(true);
  usePageTitle('Dashboard');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await estatisticasAPI.get();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const statCards = [
    { 
      title: 'Clientes', 
      value: stats.total_clientes, 
      icon: Users, 
      color: 'bg-blue-50 text-blue-600',
      link: '/clientes'
    },
    { 
      title: 'Produtos', 
      value: stats.total_produtos, 
      icon: Package, 
      color: 'bg-amber-50 text-amber-600',
      link: '/produtos'
    },
    { 
      title: 'Receitas', 
      value: stats.total_receitas, 
      icon: FileText, 
      color: 'bg-amber-50 text-amber-600',
      link: '/receitas'
    },
    { 
      title: 'Utilizadores', 
      value: stats.total_utilizadores, 
      icon: UserCog, 
      color: 'bg-purple-50 text-purple-600',
      link: '/utilizadores'
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-slate-500 mt-1">Visão geral do sistema</p>
        </div>
        <Link to="/receitas/nova">
          <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="new-receita-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nova Receita
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.title} to={stat.link}>
              <Card className="border-slate-200 hover:border-teal-300 hover:shadow-md transition-all duration-200 cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">
                        {loading ? '-' : stat.value}
                      </p>
                    </div>
                    <div className={`h-12 w-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                      <Icon className="h-6 w-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions & Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900">Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/clientes/novo" className="block">
              <Button variant="outline" className="w-full justify-between h-12 text-left" data-testid="quick-new-cliente">
                <span className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-slate-400" />
                  Adicionar Cliente
                </span>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </Button>
            </Link>
            <Link to="/produtos/novo" className="block">
              <Button variant="outline" className="w-full justify-between h-12 text-left" data-testid="quick-new-produto">
                <span className="flex items-center gap-3">
                  <Package className="h-5 w-5 text-slate-400" />
                  Adicionar Produto
                </span>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </Button>
            </Link>
            <Link to="/receitas/nova" className="block">
              <Button variant="outline" className="w-full justify-between h-12 text-left" data-testid="quick-new-receita">
                <span className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-slate-400" />
                  Criar Receita
                </span>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Receitas */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900">Receitas Recentes</CardTitle>
              <Link to="/receitas">
                <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700">
                  Ver todas
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : stats.receitas_recentes.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p>Nenhuma receita criada ainda</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.receitas_recentes.map((receita) => (
                  <Link 
                    key={receita.id} 
                    to={`/receitas/${receita.id}`}
                    className="block"
                  >
                    <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{receita.cliente_nome}</p>
                          <p className="text-sm text-slate-500">{receita.numero_receita}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Calendar className="h-4 w-4" />
                        {formatDate(receita.created_at)}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
